// Games bundle loading utility

declare global {
    interface Window {
        gamesLoaded?: boolean;
        gamesBundle?: any;
    }
}

export class GamesLoader {
    private static instance: GamesLoader;
    private loadingPromise: Promise<void> | null = null;
    private isLoaded = false;

    private constructor() {}

    static getInstance(): GamesLoader {
        if (!GamesLoader.instance) {
            GamesLoader.instance = new GamesLoader();
        }
        return GamesLoader.instance;
    }

    async loadGamesBundle(): Promise<void> {
        // If already loaded, return immediately
        if (this.isLoaded || window.gamesLoaded) {
            return;
        }

        // If already loading, return the existing promise
        if (this.loadingPromise) {
            return this.loadingPromise;
        }

        this.loadingPromise = this.loadBundle();
        return this.loadingPromise;
    }

    private async loadBundle(): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                // Create script element
                const script = document.createElement('script');
                script.type = 'module';
                script.src = '/src/components/projects/games/index.ts';

                // Handle load success
                script.onload = () => {
                    window.gamesLoaded = true;
                    this.isLoaded = true;
                    this.loadingPromise = null;
                    console.log('🎮 Games bundle loaded successfully!');
                    resolve();
                };

                // Handle load error
                script.onerror = error => {
                    console.error('❌ Failed to load games bundle:', error);
                    this.loadingPromise = null;
                    reject(new Error('Failed to load games bundle'));
                };

                // Inject script into head
                document.head.appendChild(script);
            } catch (error) {
                console.error('❌ Error injecting games bundle script:', error);
                this.loadingPromise = null;
                reject(error);
            }
        });
    }

    async loadGame(gameName: string): Promise<React.ComponentType> {
        try {
            console.log(`🎮 Loading game: ${gameName}`);

            // Import the games registry directly
            const gameModule = await import('../components/projects/games/index.ts');

            // Check if the game exists in the registry
            if (!gameModule.GAMES_REGISTRY[gameName as keyof typeof gameModule.GAMES_REGISTRY]) {
                throw new Error(`Game '${gameName}' not found in registry`);
            }

            // Load the specific game dynamically
            const GameComponent = await gameModule.GAMES_REGISTRY[gameName as keyof typeof gameModule.GAMES_REGISTRY]();
            return GameComponent;
        } catch (error) {
            console.error(`❌ Failed to load game '${gameName}':`, error);
            throw error;
        }
    }

    isGamesLoaded(): boolean {
        return this.isLoaded || !!window.gamesLoaded;
    }
}

// Export singleton instance
export const gamesLoader = GamesLoader.getInstance();
